


import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import { CustomError, CustomNotification } from "@Internal/Models";

import { Newsletters } from "App";
import { $t, EventBus } from "App/IOC";
import { Activated, Identifiable, Named } from "App/Contracts";
import { Newsletter, NewsletterContact, NewsletterPreview } from "App/Models/Newsletter";

import Icon from "Web/Components/Icon.vue";
import LoadingDots from "Web/Components/Common/LoadingDots.vue";
import PopupWindow from "Web/Components/Popup/Window.vue";

import { EmptyReason, ErrorMessage } from "./Helpers";

/* ESSAI ET APERCU D'UNE NEWSLETTER, depuis la Console ( Fred, 21/10/2026 ).

   L'apercu dit ce que contiendrait le prochain envoi : les articles candidats de
   chaque bloc, les annonces, combien de destinataires et combien auront au moins
   un contenu. Avec un contact de reference, ce que LUI recevrait ( ciblage des
   articles compris ). L'essai envoie l'e-mail a une adresse ; sans contact, il ne
   contient que les articles ouverts a tous. Le contact doit etre du groupe
   ( 404 `contact_not_found` ). */
/* Le reglage des blocs de l'e-mail, lu dans son document : type des blocs d'annonces,
   blogs et tags des blocs d'articles, par identifiant de bloc. */
interface BlockSettings {
    kinds: { [ key: string ]: string };
    totals: { [ key: string ]: string };
    articles: { [ key: string ]: { filter: string; tags: string[]; total: string } };
}

@Component({
    components: {
        Icon,
        LoadingDots,
        PopupWindow
    }
})
export default class NewsletterTest extends Vue {
    @Prop( { required: true } )
    readonly GroupId!: number;

    @Prop( { required: true } )
    readonly Record!: Newsletter;

    /* Ouverte depuis le bouton d'en-tete du reglage : posee SOUS le bouton, bord droit
       sur son bord droit ( Fred, 22/10/2026, comme au Desktop ). Coordonnees de la
       fenetre du navigateur. `null` : centree, comme depuis le menu « … ». */
    @Prop( { default: null } )
    readonly Anchor!: { top: number; right: number } | null;

    private get windowClass(): string {
        return ( this.Anchor !== null )
            ? "c-newsletter-test c-newsletter-test--anchored"
            : "c-newsletter-test";
    }

    private preview: NewsletterPreview | null = null;
    private loading: boolean = false;

    private email: string = "";
    private contact: NewsletterContact | null = null;
    private keyword: string = "";
    private timer: number = 0;
    private candidates: NewsletterContact[] = [];
    /* La recherche du mot tape a repondu : sans resultat, on le dit. */
    private searched: boolean = false;
    /* Une recherche est en route ( frappe en attente ou appel en cours ) : trois points
       orange derriere le texte ( Fred, 22/10/2026, comme au Desktop ). */
    private searching: boolean = false;

    private sending: boolean = false;


    /* « Envoyer l'essai » inactif tant qu'il n'y a pas d'adresse d'essai valable
       ( Fred, 22/10/2026 ), et pendant l'envoi. */
    private get action(): Identifiable & Named & Activated {
        return {
            Id: "send",
            Name: $t( "NEWSLETTERS_sendTest" ),
            Active: !this.sending && !this.blocked &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( this.email.trim() )
        };
    }

    beforeDestroy(): void {
        document.documentElement.style.removeProperty( "--nl-test-top" );
        document.documentElement.style.removeProperty( "--nl-test-right" );
        document.documentElement.style.removeProperty( "--nl-test-bottom" );
    }

    async mounted(): Promise<void> {
        /* La position va sur la racine du document : `Modal` deplace l'enveloppe dans
           `.modals`, et pas encore au montage — ni `this.$el` ni une recherche de
           l'enveloppe ne la trouvent ici ( mesure du 22/10/2026 : variables vides ).
           Retiree a la fermeture. */
        if( this.Anchor !== null ) {
            const root = document.documentElement;

            root.style.setProperty( "--nl-test-top", `${ this.Anchor.top }px` );
            root.style.setProperty( "--nl-test-right", `${ this.Anchor.right }px` );

            /* ⚠️ ALIGNEE SUR LE REGLAGE ( Fred, 26/10/2026 : « la modale n'est pas
               alignee avec l'autre » ) : meme bord droit et meme bas que la fenetre
               « Regler la newsletter », mesures a l'ouverture. Sans elle, le bouton. */
            const settings = document.querySelector(
                ".c-newsletter-form .c2-window"
            ) as HTMLElement | null;

            if( settings !== null ) {
                const rect = settings.getBoundingClientRect();

                const right = window.innerWidth - rect.right;
                const bottom = window.innerHeight - rect.bottom;

                root.style.setProperty( "--nl-test-right", `${ right }px` );
                root.style.setProperty( "--nl-test-bottom", `${ bottom }px` );
            }
        }

        await this.load();
    }

    private async load(): Promise<void> {
        this.loading = true;

        const [ preview, error ] = await Newsletters.Preview(
            this.GroupId, this.Record.id, this.contact !== null ? this.contact.id : undefined
        );

        this.loading = false;

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.preview = preview || null;
    }

    /* Si l'envoi partait maintenant, personne ne recevrait rien. */
    private get nothingToSend(): boolean {
        return this.preview !== null &&
            Number( this.preview.with_articles ) === 0 &&
            this.listingsCount === 0;
    }

    /* Le message dit QUOI faire : creer l'e-mail, choisir le blog, ou attendre. */
    private get nothingToSendKey(): string {
        const reason = EmptyReason( ( this.Record as any ).content_editor );

        return "NEWSLETTERS_nothingToSend" + reason;
    }

    /* GARDE-FOU ( Fred, 27/10/2026 ) : pas d'essai quand rien ne partirait — pas
       d'e-mail, bloc Articles sans blog, ou rien a mettre dedans. Le message en
       rouge de l'apercu dit pourquoi ; sans lui, le back repondait `no_article`. */
    private get blocked(): boolean {
        return this.nothingToSend ||
            EmptyReason( ( this.Record as any ).content_editor ) !== "";
    }

    private get listingsCount(): number {
        const listings: any = this.preview !== null ? this.preview.listings : undefined;

        if( listings === undefined || listings === null )
            return 0;

        return Object.values( listings ).reduce(
            ( total: number, cards: any ) =>
                total + ( Array.isArray( cards ) ? cards.length : 0 ),
            0
        );
    }

    /* LE TITRE D'UN BLOC DIT CE QU'IL CONTIENT ( Fred, 26/10/2026 : « plutot que bloc
       d'annonces 1, bloc d'annonces en vente, avec la quantite » ). Le type est reglé
       dans l'e-mail, sur le bloc ( `Attributes.Listings.Kind` : sale, rental, all, sold ),
       dont l'identifiant est la clef de l'apercu — la lecture du back
       ( `NewsletterListingBlock` ). Sans le document, « Annonces ». */
    private get blockSettings(): BlockSettings {
        const settings: BlockSettings = { kinds: {}, totals: {}, articles: {} };
        const raw: any = ( this.Record as any ).content_editor;

        if( typeof raw !== "string" || raw === "" )
            return settings;

        let document: any;

        try {
            document = JSON.parse( raw );
        } catch( e ) {
            return settings;
        }

        const names = ( items: any ): string[] => ( Array.isArray( items ) ? items : [] )
            .map( ( item: any ) => ( item !== null && typeof item === "object" ) ? item.Name : "" )
            .filter( ( name: any ) => typeof name === "string" && name !== "" );

        const walk = ( node: any ): void => {
            if( node === null || typeof node !== "object" )
                return;

            const listings = ( node.Attributes !== undefined && node.Attributes !== null )
                ? node.Attributes.Listings
                : undefined;

            if( listings !== undefined && listings !== null && typeof node.Id === "string" ) {
                settings.kinds[ node.Id ] = String( listings.Kind || "all" );
                const count = listings.Total;

                settings.totals[ node.Id ] = ( count === undefined || count === null )
                    ? ""
                    : String( count );
                return;
            }

            const post = ( node.Blueprint === "BlogPostSection" && node.Content !== undefined )
                ? node.Content.Post
                : undefined;

            if( post !== undefined && post !== null && typeof node.Id === "string" ) {
                const filter = ( post.Filter !== undefined && post.Filter !== null )
                    ? String( post.Filter.Id || "LastPublished" )
                    : "LastPublished";
                const total = ( post.Total !== null && typeof post.Total === "object" )
                    ? post.Total.Id
                    : post.Total;

                settings.articles[ node.Id ] = {
                    filter,
                    tags: names( post.Tags ),
                    total: ( total === undefined || total === null ) ? "" : String( total )
                };
                return;
            }

            Object.keys( node ).forEach( key => walk( node[ key ] ) );
        };

        walk( document );

        return settings;
    }

    private get listingKinds(): { [ key: string ]: string } {
        return this.blockSettings.kinds;
    }

    /* CE QUI A FAIT LE CHOIX ( Fred, 26/10/2026 : « on ne sait pas quelles
       caracteristiques les ont selectionnes » ) : une ligne sous le titre du bloc. */
    /* Les reglages du bloc tels que le PB e-mail les montre ( Fred, 26/10/2026 :
       « selectionner vos articles, tags de l'article, nombre d'articles » ). */
    /* La couleur du bandeau d'une annonce ( Fred, 27/10/2026 ) : exclusivite en rouge,
       vendu en bleu, le reste orange. Le back rend le TEXTE ( « Exclusivite »,
       « Vendu », « Vendu en 42 jours » ), pas un code : on le reconnait a ses mots. */
    private badgeClass( badge: string ): string {
        const text = String( badge || "" ).toLowerCase();

        if( /exclusiv/.test( text ) )
            return "c-newsletter-test__card-badge--exclusive";

        if( /vendu|sold/.test( text ) )
            return "c-newsletter-test__card-badge--sold";

        return "";
    }

    private articlesRule( block: any ): string {
        const settings = this.blockSettings.articles[ String( block.key ) ];

        if( settings === undefined )
            return "";

        /* Le choix des articles seul ( Fred, 27/10/2026 : ni tags, ni nombre ). */
        return String( this.$t( "NEWSLETTERS_ruleFilter_" + settings.filter ) );
    }

    /* « Articles (2) » : ce qui part, pas le plafond du bloc. Le numero ne sert que
       s'il y a plusieurs blocs d'articles. */
    private articlesTitle( block: any, index: number ): string {
        const count = this.sentPosts( block ).length;
        const several = this.preview !== null && this.preview.blocks.length > 1;

        return String( several
            ? this.$t( "NEWSLETTERS_articlesTitleIndexed", { index: index + 1, count } )
            : this.$t( "NEWSLETTERS_articlesTitle", { count } ) );
    }

    /* Les annonces du prochain envoi, bloc par bloc ( lot D, reponse back du 21/10 :
       `listings: { <bloc>: [ { picture, title, town, price, description, url, badge } ] }` ),
       les memes pour tous les contacts. */
    private get listingBlocks(): { Key: string; Kind: string; Cards: any[] }[] {
        const listings: any = this.preview !== null ? this.preview.listings : undefined;

        if( listings === undefined || listings === null )
            return [];

        return Object.keys( listings )
            .map( key => ( {
                Key: key,
                Kind: this.listingKinds[ key ] || "all",
                Cards: Array.isArray( listings[ key ] ) ? listings[ key ] : []
            } ) )
            .filter( block => block.Cards.length > 0 );
    }

    /* La date de publication d'un article, courte, dans la langue de l'ecran. */
    private formatDate( value: string ): string {
        const date = new Date( value );

        if( isNaN( date.getTime() ) )
            return "";

        return date.toLocaleDateString( String( this.$i18n.locale ), {
            day: "numeric", month: "short", year: "numeric"
        } );
    }

    private get contactPosts(): number[] {
        const contact: any = this.preview !== null ? this.preview.contact : undefined;

        if( contact === undefined || contact === null || contact.blocks === undefined )
            return [];

        /* `{ <bloc>: [ { id, title, … } ] }` : les articles de ce contact. */
        return ( Object.values( contact.blocks ).reduce(
            ( all: any[], posts: any ) => all.concat( Array.isArray( posts ) ? posts : [] ), []
        ) as any[] ).map( post => Number( post.id ) );
    }

    /* CE QUI PART, PAS LA RESERVE ( Fred, 26/10/2026 : « je suis cense n'avoir que deux
       articles, pourquoi tu me montres tout ? » ). Le back rend les CANDIDATS d'un bloc, du
       plus recent au plus ancien ; chaque contact recoit les `total` premiers qui le
       concernent ( `NewsletterPlanner` : un article cible ne part qu'aux contacts de son
       ciblage ). Avec un contact choisi : exactement les siens. Sans : les `total` premiers
       sans ciblage, ceux que recoit tout le monde. Le reste attend un prochain
       envoi : il ne s'affiche pas ( Fred, 26/10/2026 : « pas utile ici » ). */
    private sentPosts( block: any ): any[] {
        const candidates: any[] = Array.isArray( block.candidates ) ? block.candidates : [];

        if( this.contact !== null )
            return candidates.filter( post => this.isMine( Number( post.id ) ) );

        return candidates
            .filter( post => !this.isTargeted( post ) )
            .slice( 0, Number( block.total ) || candidates.length );
    }

    private isTargeted( post: any ): boolean {
        return post.targets !== undefined && post.targets !== null &&
            Object.keys( post.targets ).length > 0;
    }

    private isMine( id: number ): boolean {
        return this.contactPosts.indexOf( id ) >= 0;
    }

    @Watch( "keyword" )
    private onKeyword(): void {
        window.clearTimeout( this.timer );
        this.searched = false;

        if( this.keyword.trim().length < 2 ) {
            this.candidates = [];
            this.searching = false;
            return;
        }

        this.searching = true;

        this.timer = window.setTimeout( () => this.search(), 300 );
    }

    private async search(): Promise<void> {
        const keyword = this.keyword.trim();
        const [ contacts, error ] = await Newsletters.Contacts( this.GroupId, keyword, 8 );

        if( keyword !== this.keyword.trim() )
            return;

        this.searching = false;

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.candidates = contacts || [];
        this.searched = true;
    }

    private describe( contact: NewsletterContact ): string {
        const name = [ contact.firstname, contact.lastname ].filter( part => !!part ).join( " " );

        const label = [ name, contact.email ].filter( part => !!part ).join( " — " );

        return label || `#${ contact.id }`;
    }

    private async pick( contact: NewsletterContact | null ): Promise<void> {
        this.contact = contact;
        this.keyword = "";
        this.candidates = [];

        await this.load();
    }

    private fail( error: CustomError ): void {
        EventBus.Publish(
            "Error", new CustomError( error.Id, ErrorMessage( error.Message, error.SubId ) )
        );
    }

    private async send(): Promise<void> {
        if( this.blocked )
            return;

        const email = this.email.trim();

        if( this.sending || email === "" )
            return;

        this.sending = true;

        const [ , error ] = await Newsletters.Test(
            this.GroupId, this.Record.id, email, this.contact !== null ? this.contact.id : undefined
        );

        this.sending = false;

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "NEWSLETTERS_title" ), $t( "NEWSLETTERS_testSent", { email } )
        ) );
    }
}
